import { useEffect, useRef, useState } from 'react';

interface Particle {
  tx: number;
  ty: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  ox: number;
  oy: number;
  r: number;
  g: number;
  b: number;
  size: number;
  delay: number;
  intro: number;
}

interface ParticleImageProps {
  src: string;
  alt?: string;
  step?: number;
  restScatter?: number;
  introDuration?: number;
  introStagger?: number;
  className?: string;
}

export default function ParticleImage({
  src,
  alt = '',
  step = 5,
  restScatter = 6,
  introDuration = 1400,
  introStagger = 600,
  className = '',
}: ParticleImageProps) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const imageOverlayRef = useRef<HTMLImageElement | null>(null);
  const particlesRef = useRef<Particle[]>([]);
  const sizeRef = useRef<{ w: number; h: number }>({ w: 0, h: 0 });
  const imgRef = useRef<HTMLImageElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const gatherRef = useRef<number>(0);
  const hoveredRef = useRef<boolean>(false);
  const [imageReady, setImageReady] = useState(false);

  useEffect(() => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.src = src;
    image.onload = () => {
      imgRef.current = image;
      setImageReady(true);
      build();
    };
  }, [src]);

  const build = () => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    const image = imgRef.current;
    if (!wrap || !canvas || !image) return;

    const rect = wrap.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    sizeRef.current = { w: rect.width, h: rect.height };

    const off = document.createElement('canvas');
    off.width = Math.floor(rect.width);
    off.height = Math.floor(rect.height);
    const octx = off.getContext('2d', { willReadFrequently: true });
    if (!octx) return;

    const iw = image.naturalWidth;
    const ih = image.naturalHeight;
    const targetRatio = rect.width / rect.height;
    const imgRatio = iw / ih;

    let sx = 0;
    let sy = 0;
    let sw = iw;
    let sh = ih;
    if (imgRatio > targetRatio) {
      sw = ih * targetRatio;
      sx = (iw - sw) / 2;
    } else {
      sh = iw / targetRatio;
      sy = 0;
    }
    octx.drawImage(image, sx, sy, sw, sh, 0, 0, off.width, off.height);

    const data = octx.getImageData(0, 0, off.width, off.height).data;

    const particles: Particle[] = [];
    for (let y = Math.floor(step / 2); y < off.height; y += step) {
      for (let x = Math.floor(step / 2); x < off.width; x += step) {
        const idx = (y * off.width + x) * 4;
        const r = data[idx];
        const g = data[idx + 1];
        const b = data[idx + 2];
        const a = data[idx + 3];
        if (a < 10) continue;

        const angle = Math.random() * Math.PI * 2;
        const dist = Math.random() * restScatter;

        const side = Math.floor(Math.random() * 4);
        const w = rect.width;
        const h = rect.height;
        let startX = 0;
        let startY = 0;
        if (side === 0) {
          startX = Math.random() * w;
          startY = -h * 0.4 - Math.random() * h * 0.3;
        } else if (side === 1) {
          startX = w + h * 0.3 + Math.random() * w * 0.3;
          startY = Math.random() * h;
        } else if (side === 2) {
          startX = Math.random() * w;
          startY = h + h * 0.3 + Math.random() * h * 0.3;
        } else {
          startX = -w * 0.4 - Math.random() * w * 0.3;
          startY = Math.random() * h;
        }

        particles.push({
          tx: x,
          ty: y,
          x: startX,
          y: startY,
          vx: 0,
          vy: 0,
          ox: Math.cos(angle) * dist,
          oy: Math.sin(angle) * dist,
          r,
          g,
          b,
          size: step * 0.55,
          delay: Math.random() * introStagger,
          intro: 0,
        });
      }
    }
    particlesRef.current = particles;
    startTimeRef.current = performance.now();
  };

  useEffect(() => {
    const onResize = () => build();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const SPRING = 0.08;
    const FRICTION = 0.82;
    const GATHER_IN_RATE = 0.06;
    const GATHER_OUT_RATE = 0.08;

    const tick = () => {
      const { w, h } = sizeRef.current;
      if (w === 0 || h === 0) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      // Smoothly update gather progress toward target
      const target = hoveredRef.current ? 1 : 0;
      const rate = target > gatherRef.current ? GATHER_IN_RATE : GATHER_OUT_RATE;
      gatherRef.current += (target - gatherRef.current) * rate;
      if (Math.abs(target - gatherRef.current) < 0.001) gatherRef.current = target;
      const gather = gatherRef.current;
      const g01 = gather * gather * (3 - 2 * gather);

      // Sync real-image overlay opacity with gather progress
      if (imageOverlayRef.current) {
        imageOverlayRef.current.style.opacity = String(g01);
      }

      ctx.clearRect(0, 0, w, h);

      const now = performance.now();
      const elapsed = now - startTimeRef.current;

      const particles = particlesRef.current;
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];

        const localElapsed = elapsed - p.delay;
        const introT =
          localElapsed <= 0 ? 0 : Math.min(1, localElapsed / introDuration);
        p.intro = 1 - Math.pow(1 - introT, 3);

        const restX = p.tx + p.ox;
        const restY = p.ty + p.oy;
        const targetX = restX * (1 - g01) + p.tx * g01;
        const targetY = restY * (1 - g01) + p.ty * g01;

        p.vx += (targetX - p.x) * SPRING;
        p.vy += (targetY - p.y) * SPRING;
        p.vx *= FRICTION;
        p.vy *= FRICTION;
        p.x += p.vx;
        p.y += p.vy;

        if (p.intro <= 0) continue;

        // Particles fade out as gather completes, so real image shows through
        const introBoost = 1 - p.intro;
        const sizeMul = 1 + introBoost * 1.4 + (1 - g01) * 0.4;
        const size = p.size * Math.max(0.55, sizeMul);

        const baseAlpha = 0.85 * (1 - g01);
        const alpha = baseAlpha * p.intro;

        if (alpha < 0.01) continue;

        ctx.fillStyle = `rgba(${p.r}, ${p.g}, ${p.b}, ${alpha})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, size, 0, Math.PI * 2);
        ctx.fill();
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [introDuration]);

  return (
    <div
      ref={wrapRef}
      className={`relative w-full h-full overflow-hidden ${className}`}
      onPointerEnter={() => {
        hoveredRef.current = true;
      }}
      onPointerLeave={() => {
        hoveredRef.current = false;
      }}
      role="img"
      aria-label={alt}
    >
      {/* Real crisp image — fades in as gather progresses */}
      {imageReady && (
        <img
          ref={imageOverlayRef}
          src={src}
          alt={alt}
          className="absolute inset-0 w-full h-full object-cover object-top pointer-events-none"
          style={{ opacity: 0 }}
          draggable={false}
        />
      )}

      {/* Particle layer on top */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 block w-full h-full pointer-events-none"
      />
    </div>
  );
}